import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  Button,
} from '@bill-alps/ui';
import { useTranslation } from '../i18n/useTranslation';
import { useStaleBundle } from '@/hooks/use-stale-bundle';

/**
 * Shown once the tab is known to run a bundle older than the backend — either
 * the version poll saw a new build or a Convex call hit a function the deploy
 * removed (`lib/deploy-skew/stale-bundle.ts`).
 *
 * It blocks on purpose. Until the reload happens nothing the user types will
 * save, so a dismissible toast in the corner would let them keep working into a
 * void. Amber, not red: nothing failed on their side.
 */
const COUNTDOWN_SECONDS = 10;

/**
 * Reload loop guard. If a reload does not actually pick up the new build (an
 * intermediate cache still serving the old `index.html`), the dialog comes back
 * — and a countdown would spin the tab forever. One automatic reload per tab
 * session; after that the user drives.
 */
const RELOADED_FLAG = 'billAlps.staleBundleReloaded';

function autoReloadAllowed() {
  try {
    return sessionStorage.getItem(RELOADED_FLAG) === null;
  } catch {
    // Private mode / storage disabled: no way to remember, so never auto-reload.
    return false;
  }
}

function reloadNow() {
  try {
    sessionStorage.setItem(RELOADED_FLAG, '1');
  } catch {
    // Nothing to do — the guard above already refuses to auto-reload.
  }
  window.location.reload();
}

export function StaleBundleDialog() {
  const stale = useStaleBundle();
  // The dialog mounts only once the tab is stale, so its countdown can start
  // from the initial state instead of a setState inside an effect.
  return stale ? <UpdateDialog /> : null;
}

function UpdateDialog() {
  const { t } = useTranslation('common');
  const [autoReload] = useState(autoReloadAllowed);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    if (!autoReload) return;

    let remaining = COUNTDOWN_SECONDS;
    const timer = setInterval(() => {
      remaining -= 1;
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        reloadNow();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [autoReload]);

  return (
    // Controlled `open` with no `onOpenChange`: Escape, the backdrop and the
    // swipe gesture all resolve to a no-op, so there is exactly one way out.
    <AlertDialog open>
      <AlertDialogContent
        blocking
        overlayClassName="bg-primary/70"
        className="text-center"
      >
        <div
          aria-hidden
          className="bg-riso-amber-light text-riso-amber mx-auto mb-4 grid size-12 place-items-center rounded-full"
        >
          <RefreshCw className="size-6" />
        </div>
        <AlertDialogTitle className="text-xl">
          {t('staleBundle.title')}
        </AlertDialogTitle>
        <AlertDialogDescription className="mt-2 text-pretty">
          {t('staleBundle.description')}
        </AlertDialogDescription>
        <Button variant="accent" className="mt-5 w-full" onClick={reloadNow}>
          {t('staleBundle.action')}
        </Button>
        {autoReload && (
          <p className="text-muted-foreground mt-3 text-xs">
            {t('staleBundle.countdown', { seconds: Math.max(secondsLeft, 0) })}
          </p>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
